import { createContext, useContext, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const AuthCtx = createContext(null);

// Idle timeout: 12 hours of no activity → logout
const IDLE_TIMEOUT_MS = 12 * 60 * 60 * 1000;
const IDLE_CHECK_INTERVAL_MS = 60 * 1000; // every 60s
const IDLE_KEY = "wh_last_active";

// Decode JWT payload without verifying signature — used only to read `exp` for auto-logout timing.
function decodeJwtExp(token) {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return typeof payload.exp === "number" ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

function bumpActivity() {
  localStorage.setItem(IDLE_KEY, String(Date.now()));
}

function isIdleExpired() {
  const last = parseInt(localStorage.getItem(IDLE_KEY) || "0", 10);
  if (!last) return false;
  return Date.now() - last > IDLE_TIMEOUT_MS;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const expiryTimerRef = useRef(null);
  const idleIntervalRef = useRef(null);

  const clearTimers = () => {
    if (expiryTimerRef.current) {
      clearTimeout(expiryTimerRef.current);
      expiryTimerRef.current = null;
    }
    if (idleIntervalRef.current) {
      clearInterval(idleIntervalRef.current);
      idleIntervalRef.current = null;
    }
  };

  const doLogout = (reason) => {
    clearTimers();
    localStorage.removeItem("wh_token");
    localStorage.removeItem("wh_user");
    localStorage.removeItem(IDLE_KEY);
    setUser(null);
    if (reason === "expired") {
      try { toast.error("Session expired — please login again"); } catch {}
    } else if (reason === "idle") {
      try { toast.error("Logged out due to inactivity (12h). Please login again."); } catch {}
    }
    window.location.replace("/login");
  };

  const scheduleAutoLogout = (token) => {
    clearTimers();
    const expMs = decodeJwtExp(token);
    if (expMs) {
      const delay = expMs - Date.now();
      if (delay <= 0) {
        doLogout("expired");
        return;
      }
      expiryTimerRef.current = setTimeout(() => doLogout("expired"), delay);
    }
    // Idle check loop
    idleIntervalRef.current = setInterval(() => {
      if (isIdleExpired()) doLogout("idle");
    }, IDLE_CHECK_INTERVAL_MS);
  };

  // Track user activity globally
  useEffect(() => {
    if (!user) return;
    const handler = () => bumpActivity();
    const events = ["mousedown", "keydown", "scroll", "touchstart", "click"];
    events.forEach((e) => window.addEventListener(e, handler, { passive: true }));
    bumpActivity();
    return () => events.forEach((e) => window.removeEventListener(e, handler));
  }, [user]);

  useEffect(() => {
    const token = localStorage.getItem("wh_token");
    if (!token) {
      setLoading(false);
      return;
    }
    // If JWT expired or 12h idle, logout immediately
    const expMs = decodeJwtExp(token);
    if (expMs && expMs <= Date.now()) {
      doLogout("expired");
      setLoading(false);
      return;
    }
    if (isIdleExpired()) {
      doLogout("idle");
      setLoading(false);
      return;
    }
    api
      .get("/auth/me")
      .then((r) => {
        setUser(r.data);
        bumpActivity();
        scheduleAutoLogout(token);
      })
      .catch(() => {
        localStorage.removeItem("wh_token");
        localStorage.removeItem("wh_user");
        localStorage.removeItem(IDLE_KEY);
      })
      .finally(() => setLoading(false));
    return clearTimers;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const requestOtp = async (phone) => {
    const cleanPhone = (phone || "").replace(/\D/g, "");
    const { data } = await api.post("/auth/request-otp", { phone: cleanPhone });
    return data;
  };

  const verifyOtp = async (phone, otp) => {
    const cleanPhone = (phone || "").replace(/\D/g, "");
    const { data } = await api.post("/auth/verify-otp", { phone: cleanPhone, otp });
    localStorage.setItem("wh_token", data.token);
    localStorage.setItem("wh_user", JSON.stringify(data.user));
    bumpActivity();
    setUser(data.user);
    scheduleAutoLogout(data.token);
    return data.user;
  };

  const logout = () => doLogout("manual");

  return (
    <AuthCtx.Provider value={{ user, loading, requestOtp, verifyOtp, logout }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
